import { useEffect, useRef, useState } from 'react';
import { createMapAdapter } from '../maps/createAdapter';
import type { MapAdapter } from '../maps/MapAdapter';
import type { LatLng } from '../maps/types';

/** Default AU focus while waiting for a Google Places selection. */
export const ADDRESS_MAP_DEFAULT_CENTER: LatLng = { lat: -33.8688, lng: 151.2093 };

export interface AddressMapPreviewProps {
  /** Primary pin (pickup / single address). When null with no secondary, map stays at default center. */
  coordinates?: LatLng | null;
  /** Optional second pin (dropoff) — shown with a red marker. */
  secondaryCoordinates?: LatLng | null;
  title?: string;
  secondaryTitle?: string;
  height?: number;
  placeholderHint?: string;
  /**
   * When false, keep the slot but do not init the map yet
   * (e.g. Ant Modal still animating open).
   */
  active?: boolean;
}

function midpoint(a: LatLng, b: LatLng): LatLng {
  return { lat: (a.lat + b.lat) / 2, lng: (a.lng + b.lng) / 2 };
}

function applyJsPins(
  adapter: MapAdapter,
  primary: LatLng | null | undefined,
  secondary: LatLng | null | undefined,
  title: string,
  secondaryTitle: string
) {
  const markers = [];
  if (primary) {
    markers.push({
      id: 'address-pin-primary',
      position: primary,
      title,
      role: 'pickup' as const,
    });
  }
  if (secondary) {
    markers.push({
      id: 'address-pin-secondary',
      position: secondary,
      title: secondaryTitle,
      role: 'dropoff' as const,
    });
  }
  adapter.setMarkers(markers);
  if (primary && secondary) {
    adapter.fitPoints([primary, secondary]);
  } else if (primary) {
    adapter.setZoom?.(16);
    adapter.panTo(primary);
  } else if (secondary) {
    adapter.setZoom?.(16);
    adapter.panTo(secondary);
  } else {
    adapter.setZoom?.(11);
    adapter.panTo(ADDRESS_MAP_DEFAULT_CENTER);
  }
}

/**
 * Interactive Google Map preview under address fields.
 * Supports pinch / scroll / zoom-control zoom and pan.
 */
export function AddressMapPreview({
  coordinates = null,
  secondaryCoordinates = null,
  title = 'Selected location',
  secondaryTitle = 'Drop-off',
  height = 160,
  placeholderHint = 'Select an address to pin the map',
  active = true,
}: AddressMapPreviewProps) {
  const [mapFailed, setMapFailed] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const adapterRef = useRef<MapAdapter | null>(null);
  const readyRef = useRef(false);
  const primaryRef = useRef(coordinates);
  const secondaryRef = useRef(secondaryCoordinates);
  const titleRef = useRef(title);
  const secondaryTitleRef = useRef(secondaryTitle);
  primaryRef.current = coordinates;
  secondaryRef.current = secondaryCoordinates;
  titleRef.current = title;
  secondaryTitleRef.current = secondaryTitle;

  const hasPin = Boolean(coordinates || secondaryCoordinates);

  useEffect(() => {
    if (!active) {
      setMapReady(false);
      return;
    }
    let cancelled = false;

    (async () => {
      const el = containerRef.current;
      if (!el) return;
      try {
        const adapter = await createMapAdapter();
        if (cancelled) {
          adapter.destroy();
          return;
        }
        const primary = primaryRef.current;
        const secondary = secondaryRef.current;
        const center =
          primary && secondary
            ? midpoint(primary, secondary)
            : primary ?? secondary ?? ADDRESS_MAP_DEFAULT_CENTER;
        await adapter.init(el, {
          center,
          zoom: primary && secondary ? 12 : primary || secondary ? 16 : 11,
          compact: true,
          // Allow scroll-wheel / pinch zoom on the address preview.
          gestureHandling: 'greedy',
        });
        if (cancelled) {
          adapter.destroy();
          return;
        }
        applyJsPins(
          adapter,
          primaryRef.current,
          secondaryRef.current,
          titleRef.current,
          secondaryTitleRef.current
        );
        adapterRef.current = adapter;
        readyRef.current = true;
        if (!cancelled) {
          setMapFailed(false);
          setMapReady(true);
        }
      } catch {
        if (!cancelled) {
          setMapFailed(true);
          setMapReady(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      readyRef.current = false;
      setMapReady(false);
      adapterRef.current?.destroy();
      adapterRef.current = null;
    };
  }, [active]);

  useEffect(() => {
    const adapter = adapterRef.current;
    if (!adapter || !readyRef.current) return;
    applyJsPins(adapter, coordinates, secondaryCoordinates, title, secondaryTitle);
  }, [
    coordinates?.lat,
    coordinates?.lng,
    secondaryCoordinates?.lat,
    secondaryCoordinates?.lng,
    title,
    secondaryTitle,
    coordinates,
    secondaryCoordinates,
  ]);

  const showHint = !hasPin || (!mapReady && !mapFailed);

  return (
    <div style={{ marginTop: 8, position: 'relative' }}>
      <div
        ref={containerRef}
        role="application"
        aria-label={hasPin ? title : placeholderHint}
        style={{
          height,
          width: '100%',
          borderRadius: 8,
          overflow: 'hidden',
          border: '1px solid #E5E7EB',
          background: '#F3F4F6',
          // Ensure map gestures are not blocked by parent overflow rules.
          touchAction: 'none',
        }}
      />
      {mapFailed ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 12,
            color: '#64748B',
            fontSize: 12,
            background: '#F3F4F6',
            borderRadius: 8,
          }}
        >
          Map preview unavailable
        </div>
      ) : null}
      {showHint && !mapFailed ? (
        <div
          style={{
            position: 'absolute',
            left: 8,
            bottom: 8,
            right: 8,
            padding: '4px 8px',
            borderRadius: 6,
            background: 'rgba(255,255,255,0.92)',
            color: '#64748B',
            fontSize: 12,
            pointerEvents: 'none',
            boxShadow: '0 1px 2px rgba(15,23,42,0.08)',
          }}
        >
          {!hasPin ? placeholderHint : 'Loading map…'}
        </div>
      ) : null}
    </div>
  );
}

export default AddressMapPreview;
